import { useState, useRef, useEffect, useCallback, useMemo } from 'react'
import { Maximize2, Minimize2, Film, Loader2, Play, Pause } from 'lucide-react'
import { Player, type PlayerRef } from '@remotion/player'
import { prefetch } from 'remotion'
import { useVideoSeekOptional } from '@/contexts/video-seek-context'
import { Timeline } from './Timeline'
import { buildTimelineModel, type TextOverlay, type TimelineClip, type TimelineMode } from '@/lib/clipUtils'
import { ThreeTimelineComposition } from '@/remotion/ThreeTimelineComposition'
import type { AudioTrackInput, AudioMixState, CompositionProps } from '@/remotion/types'
import { secondsToFrames, framesToSeconds } from '@/remotion/utils/framemath'
import { ErrorBoundary } from '@/components/ErrorBoundary'
import { SpatialCanvas, type SpatialItem } from './SpatialCanvas'

interface WorkflowScene {
  _id: string
  title?: string
  description?: string
  videoPrompt?: string
  duration: number
  storyboardImageUrl?: string
  videoUrl?: string
  referenceFrameUrl?: string
  firstFrameUrl?: string
  isJumpCut?: boolean
  generationMode?: string
  solidColor?: string
  videoStatus: 'pending' | 'generating_image' | 'image_ready' | 'generating_video' | 'completed' | 'failed' | 'cancelled'
  keyframeStatus?: 'none' | 'generating' | 'ready' | 'failed'
  startKeyframeUrl?: string
  endKeyframeUrl?: string
  nativeAudioEnabled?: boolean
  dialog?: Array<{ characterId: string; characterName: string; text: string; tone?: string }>
  trimStart?: number
  trimEnd?: number
  speed?: number
  timelineStartTime?: number
  transition?: { type: string; duration: number }
  effects?: {
    fadeIn?: { color: string; duration: number }
    fadeOut?: { color: string; duration: number }
    colorGrade?: string
    colorGradeIntensity?: number
    brightness?: number
    contrast?: number
    saturation?: number
    hueRotate?: number
    letterbox?: boolean
    vignette?: number
    grain?: number
    blur?: number
    bloom?: number
    chromaticAberration?: number
    vhs?: boolean
    shake?: number
  }
}

interface AudioTrack {
  id: string
  type: 'music' | 'sfx' | 'voiceover'
  name: string
  url: string
  startTime: number
  duration: number
  trimStart?: number
  volume: number
  fadeIn?: number
  fadeOut?: number
  status: 'pending' | 'generating' | 'ready' | 'failed'
  volumeEnvelope?: { time: number; volume: number }[]
}

interface VideoPreviewProps {
  scenes: WorkflowScene[]
  activeSceneIndex?: number
  onSceneChange?: (index: number) => void
  audioTracks?: AudioTrack[]
  textOverlays?: TextOverlay[]
  /** Brand styling for product-demo scenes (workflow.demoStyle). */
  demoStyle?: import('@/remotion/demo/types').DemoStyle
  onAudioTrackUpdate?: (trackId: string, startTime: number, duration: number, trimStart?: number) => void
  aspectRatio?: string
  timelineMode?: TimelineMode
  onSpatialUpdate?: (itemId: string, itemType: 'clip' | 'text', changes: Record<string, number>) => void
  selectedSpatialId?: string | null
  onSpatialSelect?: (id: string | null) => void
  hideFullscreenButton?: boolean
  // Lets a parent trigger the player's native fullscreen from elsewhere (e.g. a
  // pane header button). Receives a stable toggle, null on unmount.
  onRegisterFullscreen?: (fn: (() => void) | null) => void
}

function getCompositionDimensions(aspectRatio?: string): { width: number; height: number } {
  switch (aspectRatio) {
    case '9:16': return { width: 720, height: 1280 }
    case '1:1': return { width: 1080, height: 1080 }
    case '4:5': return { width: 864, height: 1080 }
    case '4:3': return { width: 1440, height: 1080 }
    default: return { width: 1280, height: 720 }
  }
}

const FPS = 30
// How many clips ahead to prefetch as blob-urls. Wider than strictly
// needed so the browser has time to download AND parse MP4 headers
// before OffthreadVideo mounts its <video> element at clip boundaries.
const PREFETCH_AHEAD_CLIPS = 6
const EMPTY_AUDIO: AudioTrack[] = []
const EMPTY_OVERLAYS: TextOverlay[] = []

export function VideoPreview({ scenes, activeSceneIndex, onSceneChange, audioTracks = EMPTY_AUDIO, textOverlays = EMPTY_OVERLAYS, demoStyle, onAudioTrackUpdate, aspectRatio, timelineMode, onSpatialUpdate, selectedSpatialId, onSpatialSelect, hideFullscreenButton, onRegisterFullscreen }: VideoPreviewProps) {
  const [internalSceneIndex, setInternalSceneIndex] = useState(0)
  const [isFullscreen, setIsFullscreen] = useState(false)
  // Bumped to remount the Player after a composition crash — gives the user a
  // working "Reload preview" path instead of Remotion's dead-end triangle (ISS-1231).
  const [playerEpoch, setPlayerEpoch] = useState(0)
  const containerRef = useRef<HTMLDivElement>(null)
  const playerRef = useRef<PlayerRef>(null)

  const playableScenes = useMemo(
    () => scenes.filter((s) => s.videoUrl || s.storyboardImageUrl || s.startKeyframeUrl || s.firstFrameUrl || s.solidColor || (s as { demoScene?: unknown }).demoScene),
    [scenes]
  )

  const timelineModel = useMemo(
    () => buildTimelineModel(playableScenes as TimelineClip[], { mode: timelineMode }),
    [playableScenes, timelineMode]
  )
  const maxAudioEnd = useMemo(() => {
    const ready = audioTracks.filter(t => t.status === 'ready' && t.url)
    return ready.length > 0 ? Math.max(...ready.map(t => t.startTime + t.duration)) : 0
  }, [audioTracks])
  // When a voiceover/music track is ready before any clip exists, the player
  // should still mount so the audio can be auditioned ("approve the audio
  // first"). The composition's ExtraAudioRenderer plays these tracks with zero
  // scenes; totalDuration below falls back to the audio length.
  const hasReadyAudio = maxAudioEnd > 0
  const totalDuration = Math.max(timelineModel.totalDuration, maxAudioEnd)
  const totalFrames = Math.max(1, secondsToFrames(totalDuration, FPS))

  const remotionAudioTracks: AudioTrackInput[] = useMemo(() => {
    return audioTracks
      .filter(t => t.status === 'ready' && t.url)
      .map(t => ({
        id: t.id,
        type: t.type,
        url: t.url,
        startTime: t.startTime,
        duration: t.duration,
        trimStart: t.trimStart,
        volume: t.volume,
        fadeIn: t.fadeIn,
        fadeOut: t.fadeOut,
        ...(t.volumeEnvelope && { volumeEnvelope: t.volumeEnvelope }),
      }))
  }, [audioTracks])

  const videoSeekContext = useVideoSeekOptional()

  const currentSceneIndex = activeSceneIndex ?? internalSceneIndex
  const setCurrentSceneIndex = (indexOrFn: number | ((prev: number) => number)) => {
    const newIndex = typeof indexOrFn === 'function' ? indexOrFn(currentSceneIndex) : indexOrFn
    setInternalSceneIndex(newIndex)
    onSceneChange?.(newIndex)
  }

  useEffect(() => {
    if (activeSceneIndex !== undefined && activeSceneIndex !== internalSceneIndex) {
      setInternalSceneIndex(activeSceneIndex)
    }
  }, [activeSceneIndex])

  const [isPlaying, _setIsPlaying] = useState(false)
  const setIsPlaying = useCallback((val: boolean | ((prev: boolean) => boolean)) => {
    _setIsPlaying(prev => {
      const next = typeof val === 'function' ? val(prev) : val
      videoSeekContext?.updateIsPlaying(next)
      return next
    })
  }, [videoSeekContext])
  const currentTimeRef = useRef(0)
  const [displayTime, setDisplayTime] = useState(0)
  const [showControls, setShowControls] = useState(true)
  const [showSpinner, setShowSpinner] = useState(false)
  const spinnerTimerRef = useRef<NodeJS.Timeout | null>(null)
  const isPlayingRef = useRef(false)
  const hideControlsTimerRef = useRef<NodeJS.Timeout | null>(null)
  const currentScene = playableScenes[currentSceneIndex]

  useEffect(() => {
    isPlayingRef.current = isPlaying
  }, [isPlaying])

  const audioMix: AudioMixState | undefined = useMemo(() => {
    if (!videoSeekContext?.audioMix) return undefined
    return videoSeekContext.audioMix
  }, [videoSeekContext?.audioMix])

  const { width: compWidth, height: compHeight } = getCompositionDimensions(aspectRatio)
  const [containerSize, setContainerSize] = useState({ width: 0, height: 0 })
  const playerContainerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!playerContainerRef.current) return
    const obs = new ResizeObserver(([entry]) => {
      setContainerSize({ width: entry.contentRect.width, height: entry.contentRect.height })
    })
    obs.observe(playerContainerRef.current)
    return () => obs.disconnect()
  }, [])

  const spatialItems: SpatialItem[] = useMemo(() => {
    const items: SpatialItem[] = []
    const activeIdx = currentSceneIndex
    if (activeIdx >= 0 && activeIdx < playableScenes.length) {
      const s = playableScenes[activeIdx] as any
      items.push({
        id: s._id,
        type: 'clip',
        label: s.title || `Clip ${activeIdx + 1}`,
        x: s.spatialX ?? 0,
        y: s.spatialY ?? 0,
        width: s.spatialWidth ?? 100,
        height: s.spatialHeight ?? 100,
        rotation: s.spatialRotation ?? 0,
        zIndex: s.spatialZIndex ?? 0,
        opacity: s.spatialOpacity ?? 1,
        thumbnailUrl: s.firstFrameUrl || s.storyboardImageUrl,
      })
    }
    const entry = timelineModel.entries[activeIdx]
    const clipStart = entry?.globalStart ?? 0
    const clipEnd = clipStart + (entry?.effectiveDuration ?? 0)
    let textIdx = 0
    for (const overlay of textOverlays) {
      const oEnd = overlay.startTime + overlay.duration
      if (overlay.startTime < clipEnd && oEnd > clipStart) {
        const defaultY = 70 - textIdx * 12
        items.push({
          id: overlay.id,
          type: 'text',
          label: overlay.text.slice(0, 30),
          x: overlay.spatialX ?? 10,
          y: overlay.spatialY ?? Math.max(5, defaultY),
          width: overlay.spatialWidth ?? 80,
          height: overlay.spatialHeight ?? 10,
          rotation: overlay.spatialRotation ?? 0,
          zIndex: 50 + textIdx,
          opacity: 1,
        })
        textIdx++
      }
    }
    return items
  }, [playableScenes, textOverlays, currentSceneIndex, timelineModel])

  const [liveSpatialOverride, setLiveSpatialOverride] = useState<{ id: string } & Partial<SpatialItem> | null>(null)

  const handleSpatialItemUpdate = useCallback((id: string, changes: Partial<SpatialItem>) => {
    setLiveSpatialOverride(null)
    const isClip = playableScenes.some((s: any) => s._id === id)
    const numericChanges: Record<string, number> = {}
    if (changes.x != null) numericChanges.spatialX = changes.x
    if (changes.y != null) numericChanges.spatialY = changes.y
    if (changes.width != null) numericChanges.spatialWidth = changes.width
    if (changes.height != null) numericChanges.spatialHeight = changes.height
    if (changes.rotation != null) numericChanges.spatialRotation = changes.rotation
    if (changes.zIndex != null) numericChanges.spatialZIndex = changes.zIndex
    if (changes.opacity != null) numericChanges.spatialOpacity = changes.opacity
    onSpatialUpdate?.(id, isClip ? 'clip' : 'text', numericChanges)
  }, [playableScenes, onSpatialUpdate])

  const handleSpatialLiveDrag = useCallback((id: string, changes: Partial<SpatialItem>) => {
    setLiveSpatialOverride({ id, ...changes })
  }, [])

  const sharedAudioTagCount = useMemo(() => {
    if (typeof navigator === 'undefined') return 8
    return /iPad|iPhone|iPod/.test(navigator.userAgent) ? 8 : 0
  }, [])

  const liveClips = useMemo(() => {
    if (!liveSpatialOverride) return playableScenes as TimelineClip[]
    return (playableScenes as TimelineClip[]).map((clip: any) => {
      if (clip._id !== liveSpatialOverride.id) return clip
      return {
        ...clip,
        ...(liveSpatialOverride.x != null && { spatialX: liveSpatialOverride.x }),
        ...(liveSpatialOverride.y != null && { spatialY: liveSpatialOverride.y }),
        ...(liveSpatialOverride.width != null && { spatialWidth: liveSpatialOverride.width }),
        ...(liveSpatialOverride.height != null && { spatialHeight: liveSpatialOverride.height }),
        ...(liveSpatialOverride.rotation != null && { spatialRotation: liveSpatialOverride.rotation }),
      }
    })
  }, [playableScenes, liveSpatialOverride])

  const compositionProps: CompositionProps = useMemo(() => ({
    clips: liveClips,
    textOverlays,
    audioTracks: remotionAudioTracks,
    audioMix,
    timelineMode,
    demoStyle,
    fps: FPS,
    width: compWidth,
    height: compHeight,
  }), [liveClips, textOverlays, remotionAudioTracks, audioMix, timelineMode, demoStyle, compWidth, compHeight])

  // Prefetch video URLs for clips near playhead + audio tracks as blob-urls.
  // The actual pre-warming of video elements happens via Remotion's premountFor
  // on the Sequence in ThreeTimelineComposition — this just ensures the blob
  // data is downloaded before the premount activates.
  const prefetchHandlesRef = useRef<Map<string, { free: () => void }>>(new Map())
  const prefetchedUrlsRef = useRef<Set<string>>(new Set())
  const [prefetchDebugVersion, setPrefetchDebugVersion] = useState(0)

  useEffect(() => {
    const handles = prefetchHandlesRef.current
    const entries = timelineModel.entries

    const startIdx = Math.max(0, currentSceneIndex - 2)
    const endIdx = Math.min(entries.length, currentSceneIndex + PREFETCH_AHEAD_CLIPS + 1)

    const desiredUrls = new Set<string>()
    for (let i = startIdx; i < endIdx; i++) {
      const clip = entries[i]?.clip
      const src = clip?.videoUrl
      if (src && clip?.videoStatus === 'completed') desiredUrls.add(src)
    }
    for (const track of remotionAudioTracks) {
      if (track.url) desiredUrls.add(track.url)
    }

    let changed = false
    for (const [url, handle] of handles) {
      if (!desiredUrls.has(url)) {
        handle.free()
        handles.delete(url)
        prefetchedUrlsRef.current.delete(url)
        changed = true
        console.debug('[Playback] prefetch evict', url.slice(-40), `(${handles.size} active)`)
      }
    }

    for (const url of desiredUrls) {
      if (!handles.has(url)) {
        changed = true
        const handle = prefetch(url, { method: 'blob-url' })
        handles.set(url, handle)
        console.debug('[Playback] prefetch add', url.slice(-40), `(${handles.size} active)`)
        if ('waitUntilDone' in handle) {
          ;(handle as { waitUntilDone?: () => Promise<string> }).waitUntilDone?.()
            .then(() => {
              if (!handles.has(url)) return
              if (prefetchedUrlsRef.current.has(url)) return
              prefetchedUrlsRef.current.add(url)
              setPrefetchDebugVersion((v) => v + 1)
            })
            .catch(() => {})
        }
      }
    }
    if (changed) setPrefetchDebugVersion((v) => v + 1)
  }, [timelineModel, currentSceneIndex, remotionAudioTracks])

  // Clean up all prefetch handles on unmount
  useEffect(() => {
    return () => {
      for (const handle of prefetchHandlesRef.current.values()) {
        handle.free()
      }
      prefetchHandlesRef.current.clear()
      prefetchedUrlsRef.current.clear()
    }
  }, [])

  // Push buffer debug data to seek context for Timeline's debug overlay.
  // Use a ref for the callback to avoid re-triggering when context updates.
  const updateBufferDebugRef = useRef(videoSeekContext?.updateBufferDebug)
  updateBufferDebugRef.current = videoSeekContext?.updateBufferDebug

  useEffect(() => {
    const update = updateBufferDebugRef.current
    if (!update) return
    const entries = timelineModel.entries
    const handles = prefetchHandlesRef.current
    const prefetchedUrls = prefetchedUrlsRef.current
    const prefetched: Array<{ start: number; end: number }> = []
    const pending: Array<{ start: number; end: number }> = []
    let readyCount = 0
    let totalCount = 0

    for (const entry of entries) {
      const url = entry.clip.videoUrl
      if (!url || entry.clip.videoStatus !== 'completed') continue
      totalCount++
      if (prefetchedUrls.has(url)) {
        prefetched.push({ start: entry.globalStart, end: entry.globalEnd })
        readyCount++
      } else if (handles.has(url)) {
        pending.push({ start: entry.globalStart, end: entry.globalEnd })
      }
    }

    update({ prefetched, pending, readyCount, totalCount })
  }, [timelineModel, currentSceneIndex, prefetchDebugVersion])

  // Derive current scene from time
  const currentSceneIndexRef = useRef(currentSceneIndex)
  currentSceneIndexRef.current = currentSceneIndex
  const onSceneChangeRef = useRef(onSceneChange)
  onSceneChangeRef.current = onSceneChange
  const updateSceneFromTime = useCallback((globalSeconds: number) => {
    for (let i = timelineModel.entries.length - 1; i >= 0; i--) {
      const entry = timelineModel.entries[i]
      if (globalSeconds >= entry.globalStart) {
        if (i !== currentSceneIndexRef.current) {
          setInternalSceneIndex(i)
          onSceneChangeRef.current?.(i)
        }
        break
      }
    }
  }, [timelineModel.entries])

  // Player event handlers -- simple, using Remotion's native buffer state
  useEffect(() => {
    const player = playerRef.current
    if (!player) return

    const handlePlay = () => { setIsPlaying(true) }
    const handlePause = () => { setIsPlaying(false) }
    const handleEnded = () => {
      setIsPlaying(false)
      setShowSpinner(false)
      setCurrentSceneIndex(0)
      currentTimeRef.current = 0
    }
    const handleTimeUpdate = (e: { detail: { frame: number } }) => {
      const seconds = framesToSeconds(e.detail.frame, FPS)
      currentTimeRef.current = seconds
      setDisplayTime(seconds)
      videoSeekContext?.updateCurrentTime(seconds)
      updateSceneFromTime(seconds)
    }
    const handleWaiting = () => {
      console.debug('[Playback] buffering — waiting for data')
      // Debounce: only show spinner if stall persists > 800ms.
      // Brief stalls at clip boundaries (premount seek, frame decode) are
      // imperceptible — the player holds the last frame. The spinner
      // draws attention to what would otherwise be invisible.
      if (!spinnerTimerRef.current) {
        spinnerTimerRef.current = setTimeout(() => setShowSpinner(true), 800)
      }
    }
    const handleResume = () => {
      console.debug('[Playback] buffering resolved — resuming')
      if (spinnerTimerRef.current) {
        clearTimeout(spinnerTimerRef.current)
        spinnerTimerRef.current = null
      }
      setShowSpinner(false)
    }
    const handleError = (e: { detail: { error: Error } }) => {
      console.error('[VideoPreview] composition error:', e.detail.error)
    }

    player.addEventListener('play', handlePlay)
    player.addEventListener('pause', handlePause)
    player.addEventListener('ended', handleEnded)
    player.addEventListener('timeupdate', handleTimeUpdate as any)
    player.addEventListener('waiting', handleWaiting)
    player.addEventListener('resume', handleResume)
    player.addEventListener('error', handleError as any)

    return () => {
      player.removeEventListener('play', handlePlay)
      player.removeEventListener('pause', handlePause)
      player.removeEventListener('ended', handleEnded)
      player.removeEventListener('timeupdate', handleTimeUpdate as any)
      player.removeEventListener('waiting', handleWaiting)
      player.removeEventListener('resume', handleResume)
      player.removeEventListener('error', handleError as any)
    }
  }, [videoSeekContext, updateSceneFromTime, setIsPlaying])


  // Seek helpers
  const seekToGlobalTime = useCallback((globalSeconds: number) => {
    const frame = secondsToFrames(globalSeconds, FPS)
    currentTimeRef.current = globalSeconds
    playerRef.current?.seekTo(frame)
  }, [])

  const scrubToGlobalTime = useCallback((globalSeconds: number) => {
    const frame = secondsToFrames(globalSeconds, FPS)
    currentTimeRef.current = globalSeconds
    playerRef.current?.seekTo(frame)
    playerRef.current?.pause()
  }, [])

  const handleScrubStart = useCallback(() => {
    playerRef.current?.pause()
    setIsPlaying(false)
  }, [setIsPlaying])

  const handleScrubEnd = useCallback(() => {
  }, [])

  // Play range
  const playRangeRef = useRef<{ end: number; checkInterval: NodeJS.Timeout } | null>(null)
  const playRange = useCallback((startSeconds: number, endSeconds: number) => {
    if (playRangeRef.current) {
      clearInterval(playRangeRef.current.checkInterval)
      playRangeRef.current = null
    }

    seekToGlobalTime(startSeconds)
    playerRef.current?.play()
    setIsPlaying(true)

    const checkInterval = setInterval(() => {
      const frame = playerRef.current?.getCurrentFrame() ?? 0
      const currentSec = framesToSeconds(frame, FPS)
      if (currentSec >= endSeconds) {
        playerRef.current?.pause()
        setIsPlaying(false)
        if (playRangeRef.current) {
          clearInterval(playRangeRef.current.checkInterval)
          playRangeRef.current = null
        }
      }
    }, 100)

    playRangeRef.current = { end: endSeconds, checkInterval }
  }, [seekToGlobalTime, setIsPlaying])

  // Register handlers with seek context
  useEffect(() => {
    if (videoSeekContext) {
      videoSeekContext.registerSeekHandler(seekToGlobalTime)
      videoSeekContext.registerPlayRangeHandler(playRange)
      videoSeekContext.registerScrubHandler(scrubToGlobalTime)
      videoSeekContext.registerScrubStartHandler(handleScrubStart)
      videoSeekContext.registerScrubEndHandler(handleScrubEnd)
    }
  }, [videoSeekContext, seekToGlobalTime, playRange, scrubToGlobalTime, handleScrubStart, handleScrubEnd])

  useEffect(() => {
    if (videoSeekContext?.pendingAction) {
      const action = videoSeekContext.pendingAction
      videoSeekContext.clearPendingAction()
      if (action.type === 'seek') {
        seekToGlobalTime(action.start)
      } else if (action.type === 'playRange' && action.end !== undefined) {
        playRange(action.start, action.end)
      }
    }
  }, [videoSeekContext?.pendingAction, seekToGlobalTime, playRange, videoSeekContext])

  useEffect(() => {
    return () => {
      if (playRangeRef.current) clearInterval(playRangeRef.current.checkInterval)
    }
  }, [])

  const handlePlayPause = () => {
    if (isPlaying) {
      playerRef.current?.pause()
    } else {
      const atEnd = currentTimeRef.current >= totalDuration - 0.1
      if (atEnd) {
        playerRef.current?.seekTo(0)
        currentTimeRef.current = 0
        setCurrentSceneIndex(0)
      }
      playerRef.current?.play()
    }
  }

  const handlePlayPauseRef = useRef(handlePlayPause)
  handlePlayPauseRef.current = handlePlayPause
  useEffect(() => {
    if (videoSeekContext) {
      videoSeekContext.registerPlayPauseHandler(() => handlePlayPauseRef.current())
    }
  }, [videoSeekContext])

  const handleFullscreen = useCallback(() => {
    if (!containerRef.current) return
    if (document.fullscreenElement) {
      document.exitFullscreen()
    } else {
      containerRef.current.requestFullscreen()
    }
  }, [])

  useEffect(() => {
    const handleFullscreenChange = () => setIsFullscreen(!!document.fullscreenElement)
    document.addEventListener('fullscreenchange', handleFullscreenChange)
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange)
  }, [])

  useEffect(() => {
    onRegisterFullscreen?.(handleFullscreen)
    return () => onRegisterFullscreen?.(null)
  }, [onRegisterFullscreen, handleFullscreen])

  const handleMouseMove = useCallback(() => {
    setShowControls(true)
    if (hideControlsTimerRef.current) clearTimeout(hideControlsTimerRef.current)
    if (isPlaying) {
      hideControlsTimerRef.current = setTimeout(() => setShowControls(false), 2500)
    }
  }, [isPlaying])

  const handleMouseLeave = useCallback(() => {
    if (isPlaying) setShowControls(false)
  }, [isPlaying])

  useEffect(() => {
    if (!isPlaying) {
      setShowControls(true)
      if (hideControlsTimerRef.current) clearTimeout(hideControlsTimerRef.current)
    }
  }, [isPlaying])

  useEffect(() => {
    return () => {
      if (hideControlsTimerRef.current) clearTimeout(hideControlsTimerRef.current)
    }
  }, [])

  // Keyboard shortcuts in fullscreen
  useEffect(() => {
    if (!isFullscreen) return
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === ' ' || e.key === 'k') {
        e.preventDefault()
        handlePlayPause()
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault()
        const t = Math.max(0, currentTimeRef.current - 5)
        seekToGlobalTime(t)
        setDisplayTime(t)
      } else if (e.key === 'ArrowRight') {
        e.preventDefault()
        const t = Math.min(totalDuration, currentTimeRef.current + 5)
        seekToGlobalTime(t)
        setDisplayTime(t)
      }
    }
    window.addEventListener('keydown', handleKey)
    return () => window.removeEventListener('keydown', handleKey)
  }, [isFullscreen, handlePlayPause, seekToGlobalTime, totalDuration])

  // Seek bar interaction for fullscreen controls
  const seekBarRef = useRef<HTMLDivElement>(null)
  const handleSeekBarInteraction = useCallback((clientX: number) => {
    const bar = seekBarRef.current
    if (!bar) return
    const rect = bar.getBoundingClientRect()
    const fraction = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width))
    const time = fraction * totalDuration
    seekToGlobalTime(time)
    setDisplayTime(time)
  }, [totalDuration, seekToGlobalTime])

  const isScrubbing = useRef(false)
  const handleSeekBarMouseDown = useCallback((e: React.MouseEvent) => {
    e.stopPropagation()
    isScrubbing.current = true
    playerRef.current?.pause()
    handleSeekBarInteraction(e.clientX)

    const onMove = (ev: MouseEvent) => handleSeekBarInteraction(ev.clientX)
    const onUp = () => {
      isScrubbing.current = false
      window.removeEventListener('mousemove', onMove)
      window.removeEventListener('mouseup', onUp)
    }
    window.addEventListener('mousemove', onMove)
    window.addEventListener('mouseup', onUp)
  }, [handleSeekBarInteraction])

  if (scenes.length === 0 && !hasReadyAudio) {
    return (
      <div className="h-full flex flex-col bg-muted">
        <div className="flex-1 flex items-center justify-center bg-surface">
          <div className="text-center">
            <Film size={32} className="text-surface-muted mx-auto mb-3" />
            <p className="text-sm text-surface-muted">Your preview will appear here</p>
          </div>
        </div>
        <Timeline
          scenes={[]}
          audioTracks={audioTracks}
          currentTime={0}
          totalDuration={totalDuration || 30}
          onSeek={() => {}}
          onAudioTrackUpdate={onAudioTrackUpdate}
        />
      </div>
    )
  }

  if (playableScenes.length === 0 && !hasReadyAudio) {
    return (
      <div className="h-full flex flex-col">
        <div className="flex-1 p-4 overflow-y-auto">
          <div className="space-y-2">
            {scenes.map((scene, index) => (
              <div key={scene._id} className="flex items-center gap-3 p-3 rounded-lg bg-background border border-border">
                <div className="w-20 h-11 bg-muted rounded overflow-hidden shrink-0 flex items-center justify-center">
                  <Film size={16} className="text-muted-foreground/50" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-foreground truncate">{scene.title || `Clip ${index + 1}`}</p>
                  <p className="text-xs text-muted-foreground">{scene.duration}s</p>
                </div>
                <span className={`text-xs px-2 py-1 rounded-full font-medium ${
                  scene.videoStatus === 'failed' ? 'bg-destructive/10 text-destructive' :
                  scene.videoStatus === 'generating_image' || scene.videoStatus === 'generating_video' ? 'bg-primary/10 text-primary' :
                  'bg-muted text-muted-foreground'
                }`}>
                  {scene.videoStatus === 'failed' ? 'Failed' :
                   scene.videoStatus === 'generating_image' ? 'Sketching...' :
                   scene.videoStatus === 'generating_video' ? 'Rendering...' : 'Queued'}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    )
  }

  return (
    <div ref={containerRef} className="flex flex-col h-full bg-black">
      <div
        className="relative flex-1 min-h-0 flex items-center justify-center cursor-pointer bg-black"
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
      >
        <div ref={playerContainerRef} className="relative w-full h-full flex items-center justify-center" onClick={() => {
          onSpatialSelect?.(null)
        }}>
          <ErrorBoundary level="section" resetKey={playableScenes.length}>
            <Player
              key={playerEpoch}
              ref={playerRef}
              component={ThreeTimelineComposition as unknown as React.ComponentType<Record<string, unknown>>}
              compositionWidth={getCompositionDimensions(aspectRatio).width}
              compositionHeight={getCompositionDimensions(aspectRatio).height}
              fps={FPS}
              durationInFrames={totalFrames}
              inputProps={compositionProps as unknown as Record<string, unknown>}
              style={{ width: '100%', height: '100%', backgroundColor: 'black' }}
              controls={false}
              clickToPlay={false}
              numberOfSharedAudioTags={sharedAudioTagCount}
              bufferStateDelayInMilliseconds={200}
              errorFallback={({ error }) => (
                <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-black text-center px-6">
                  <p className="text-sm text-white/80">Preview hit a playback error.</p>
                  <p className="text-xs text-white/40 max-w-md truncate">{error.message}</p>
                  <button
                    onClick={(ev) => { ev.stopPropagation(); setPlayerEpoch((n) => n + 1) }}
                    className="px-3 py-1.5 text-xs font-medium rounded bg-white/10 hover:bg-white/20 text-white transition-colors"
                  >
                    Reload preview
                  </button>
                </div>
              )}
            />
          </ErrorBoundary>
          {(onSpatialSelect || onSpatialUpdate) && (
            <SpatialCanvas
              items={spatialItems}
              selectedId={selectedSpatialId ?? null}
              onSelect={(id) => onSpatialSelect?.(id)}
              onUpdate={handleSpatialItemUpdate}
              onLiveUpdate={handleSpatialLiveDrag}
              containerWidth={containerSize.width}
              containerHeight={containerSize.height}
              compositionWidth={compWidth}
              compositionHeight={compHeight}
            />
          )}
        </div>

        {/* Buffering spinner */}
        {showSpinner && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-20">
            <div className="bg-black/40 rounded-full p-3">
              <Loader2 size={24} className="text-white animate-spin" />
            </div>
          </div>
        )}

        {/* Fullscreen button */}
        {!hideFullscreenButton && (
          <button
            onClick={handleFullscreen}
            className={`absolute top-1.5 right-1.5 p-1 bg-black/40 hover:bg-black/60 rounded transition-all duration-300 ${
              showControls ? 'opacity-100' : 'opacity-0 pointer-events-none'
            }`}
          >
            {isFullscreen ? <Minimize2 size={12} className="text-white/80" /> : <Maximize2 size={12} className="text-white/80" />}
          </button>
        )}

        {/* Preview label with top gradient — only show for storyboard images */}
        {currentScene && !currentScene.videoUrl && currentScene.storyboardImageUrl && (
          <div className="absolute top-0 left-0 right-0 bg-gradient-to-b from-black/60 to-transparent pt-2 pb-6 pointer-events-none z-10">
            <span className="block text-center text-sm font-medium tracking-wider text-white/70 uppercase">Preview image</span>
          </div>
        )}

        {/* Clip label overlay — only show for sketches, not when real video is playing */}
        {currentScene && !currentScene.videoUrl && !isFullscreen && (
          <div className={`absolute bottom-0 left-0 right-0 bg-linear-to-t from-black/60 to-transparent px-3 pb-2 pt-6 transition-all duration-300 ${
            showControls ? 'opacity-100' : 'opacity-0 pointer-events-none'
          }`}>
            <div className="text-center">
              <span className="text-[10px] text-white/70 font-medium tracking-wide">
                Clip {currentSceneIndex + 1}{currentScene?.title ? ` — ${currentScene.title}` : ''}
              </span>
            </div>
          </div>
        )}

        {/* Fullscreen playback controls */}
        {isFullscreen && (
          <div
            className={`absolute bottom-0 left-0 right-0 transition-all duration-300 ${
              showControls ? 'opacity-100' : 'opacity-0 pointer-events-none'
            }`}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="bg-gradient-to-t from-black/80 via-black/40 to-transparent pt-16 pb-6 px-6">
              {/* Seek bar */}
              <div
                ref={seekBarRef}
                className="group/seek relative h-1 hover:h-1.5 bg-white/20 rounded-full cursor-pointer transition-all mb-4"
                onMouseDown={handleSeekBarMouseDown}
              >
                <div
                  className="absolute inset-y-0 left-0 bg-white rounded-full"
                  style={{ width: totalDuration > 0 ? `${(displayTime / totalDuration) * 100}%` : '0%' }}
                />
                <div
                  className="absolute top-1/2 -translate-y-1/2 w-3 h-3 bg-white rounded-full opacity-0 group-hover/seek:opacity-100 transition-opacity"
                  style={{ left: totalDuration > 0 ? `${(displayTime / totalDuration) * 100}%` : '0%', marginLeft: '-6px' }}
                />
              </div>

              {/* Controls row */}
              <div className="flex items-center gap-4">
                <button
                  onClick={handlePlayPause}
                  className="text-white hover:text-white/80 transition-colors"
                >
                  {isPlaying
                    ? <Pause size={22} fill="white" />
                    : <Play size={22} fill="white" />
                  }
                </button>

                <span className="text-white/80 text-xs font-mono tabular-nums select-none">
                  {formatTime(displayTime)} / {formatTime(totalDuration)}
                </span>

                <div className="flex-1" />

                <button
                  onClick={handleFullscreen}
                  className="text-white/70 hover:text-white transition-colors"
                >
                  <Minimize2 size={18} />
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

function formatTime(seconds: number): string {
  const m = Math.floor(seconds / 60)
  const s = Math.floor(seconds % 60)
  return `${m}:${s.toString().padStart(2, '0')}`
}
